import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import { BillingFacts, TransactionsTable, formatDateTime, formatMoney } from './billingShared';
import { studentLabel } from '../utils/studentLabel';
import { useStudentTabIdentity } from '../hooks/useTabIdentity';

function emptyLessonForm(rate = '') {
  return {
    topic: '',
    description: '',
    homework: '',
    minutes: '60',
    lessonRate: rate || '',
  };
}

export default function TeacherStudentBillingPage() {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useStudentTabIdentity(
    data
      ? { ...data, id: data.id ?? Number(studentId) }
      : null,
    'счёт',
  );

  const [rate, setRate] = useState('');
  const [deposit, setDeposit] = useState('');
  const [depositComment, setDepositComment] = useState('Ручное пополнение');
  const [saving, setSaving] = useState('');

  const [form, setForm] = useState(() => emptyLessonForm());
  const [editingId, setEditingId] = useState(null);
  const [editingOriginalAmount, setEditingOriginalAmount] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/payments/teacher/students/${studentId}/`);
      setData(res.data);
      setRate(res.data.rate_per_hour || '');
      setForm((prev) => (
        editingId
          ? prev
          : emptyLessonForm(res.data.rate_per_hour || '')
      ));
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить счёт ученика.');
    } finally {
      setLoading(false);
    }
  }, [studentId, editingId]);

  useEffect(() => {
    load();
  }, [load]);

  const previewAmount = useMemo(() => {
    const r = Number(form.lessonRate);
    const m = Number(form.minutes);
    if (!Number.isFinite(r) || !Number.isFinite(m) || m <= 0 || r <= 0) return '';
    return ((r * m) / 60).toFixed(2);
  }, [form.lessonRate, form.minutes]);

  function setFormField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function startEdit(report) {
    setEditingId(report.id);
    setEditingOriginalAmount(report.amount);
    setEditingStatus(report.status);
    setForm({
      topic: report.topic || '',
      description: report.description || '',
      homework: report.homework || '',
      minutes: String(report.duration_minutes || 60),
      lessonRate: report.rate_per_hour || data?.rate_per_hour || '',
    });
    setError('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingOriginalAmount(null);
    setEditingStatus(null);
    setForm(emptyLessonForm(data?.rate_per_hour || rate || ''));
  }

  async function saveRate(e) {
    e.preventDefault();
    setSaving('rate');
    setError('');
    try {
      await api.post('/payments/teacher/rates/', {
        student_id: Number(studentId),
        rate_per_hour: rate,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить ставку.');
    } finally {
      setSaving('');
    }
  }

  async function saveDeposit(e) {
    e.preventDefault();
    setSaving('deposit');
    setError('');
    try {
      await api.post('/payments/teacher/deposit/', {
        student_id: Number(studentId),
        amount: deposit,
        description: depositComment,
      });
      setDeposit('');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось зачислить.');
    } finally {
      setSaving('');
    }
  }

  async function submitLesson(e) {
    e.preventDefault();
    setSaving('report');
    setError('');
    try {
      if (editingId) {
        const amountChanged =
          editingStatus === 'charged'
          && previewAmount
          && Number(previewAmount) !== Number(editingOriginalAmount);
        if (amountChanged) {
          const ok = window.confirm(
            `Счёт ученика сразу станет ${formatMoney(previewAmount)} вместо ${formatMoney(editingOriginalAmount)}. Продолжить?`,
          );
          if (!ok) {
            setSaving('');
            return;
          }
        }
        await api.patch(`/payments/teacher/lesson-reports/${editingId}/`, {
          topic: form.topic,
          description: form.description,
          homework: form.homework,
          duration_minutes: Number(form.minutes),
          rate_per_hour: form.lessonRate,
        });
        cancelEdit();
      } else {
        await api.post('/payments/teacher/lesson-reports/', {
          student_id: Number(studentId),
          topic: form.topic,
          description: form.description,
          homework: form.homework,
          duration_minutes: Number(form.minutes),
          rate_per_hour: form.lessonRate,
        });
        setForm(emptyLessonForm(data?.rate_per_hour || rate || ''));
      }
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || (editingId ? 'Не удалось сохранить изменения.' : 'Не удалось сохранить занятие.'));
    } finally {
      setSaving('');
    }
  }

  async function cancelReport(id) {
    setSaving(`cancel-${id}`);
    try {
      await api.patch(`/payments/teacher/lesson-reports/${id}/`, { status: 'cancelled' });
      if (editingId === id) cancelEdit();
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось отменить.');
    } finally {
      setSaving('');
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка…</p>
      </div>
    );
  }

  const amountWillChange =
    editingId
    && editingStatus === 'charged'
    && previewAmount
    && Number(previewAmount) !== Number(editingOriginalAmount);

  return (
    <div className="billing-page">
      <div className="page-header">
        <p className="text-secondary">
          <Link to="/teacher/billing">← Все ученики</Link>
        </p>
        <h1>{data ? studentLabel(data) : 'Ученик'}</h1>
        {data?.username && <p className="page-subtitle">@{data.username}</p>}
        <p className="page-subtitle">Семейный счёт, ставка, журнал занятий.</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      <BillingFacts
        data={data}
        extras={[
          {
            label: 'Ставка',
            value: data?.rate_per_hour ? `${formatMoney(data.rate_per_hour)} / час` : 'не задана',
          },
        ]}
      />

      {data?.members?.length > 0 && (
        <p className="text-secondary">
          Счёт: {data.members.map((m) => m.display_name || m.username).join(', ')}
        </p>
      )}

      <section className="billing-section billing-forms">
        <form onSubmit={saveRate} className="billing-form-card">
          <h2>Ставка за час</h2>
          <label>
            ₽ / 60 минут
            <input
              className="form-input"
              type="number"
              min="1"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="btn btn-outline" disabled={saving === 'rate'}>
            Сохранить ставку
          </button>
        </form>

        <form onSubmit={saveDeposit} className="billing-form-card">
          <h2>Ручное пополнение</h2>
          <label>
            Сумма, ₽
            <input
              className="form-input"
              type="number"
              step="0.01"
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
              required
            />
          </label>
          <label>
            Комментарий
            <input
              className="form-input"
              value={depositComment}
              onChange={(e) => setDepositComment(e.target.value)}
            />
          </label>
          <button type="submit" className="btn btn-outline" disabled={saving === 'deposit'}>
            Зачислить
          </button>
        </form>
      </section>

      <section className="billing-section">
        <h2>{editingId ? 'Изменить занятие' : 'Оформить занятие'}</h2>
        <form onSubmit={submitLesson} className="billing-form-card billing-form-card--wide">
          <label>
            Тема
            <input
              className="form-input"
              value={form.topic}
              onChange={(e) => setFormField('topic', e.target.value)}
            />
          </label>
          <label>
            Что делали
            <textarea
              className="form-input"
              rows={3}
              value={form.description}
              onChange={(e) => setFormField('description', e.target.value)}
            />
          </label>
          <label>
            Домашнее задание
            <textarea
              className="form-input"
              rows={2}
              value={form.homework}
              onChange={(e) => setFormField('homework', e.target.value)}
            />
          </label>
          <div className="billing-form-row">
            <label>
              Длительность, мин
              <input
                className="form-input"
                type="number"
                min="1"
                value={form.minutes}
                onChange={(e) => setFormField('minutes', e.target.value)}
                required
              />
            </label>
            <label>
              Ставка, ₽ / час
              <input
                className="form-input"
                type="number"
                min="1"
                step="0.01"
                value={form.lessonRate}
                onChange={(e) => setFormField('lessonRate', e.target.value)}
                required
              />
            </label>
          </div>
          {!editingId && (
            <p className="text-secondary">
              Ставка подставляется из профиля. Для группового занятия можно поменять только здесь — в профиле не сохранится.
            </p>
          )}
          <p>
            {editingId
              ? (editingStatus === 'charged' ? 'Сумма списания:' : 'К списанию после подтверждения:')
              : 'К списанию после подтверждения учеником:'}
            {' '}
            <strong>{previewAmount ? formatMoney(previewAmount) : '—'}</strong>
          </p>
          {amountWillChange && (
            <p className="billing-edit-warn">
              Счёт ученика сразу станет {formatMoney(previewAmount)} вместо {formatMoney(editingOriginalAmount)}.
            </p>
          )}
          <div className="billing-form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving === 'report'}>
              {editingId ? 'Сохранить изменения' : 'Сохранить и отправить ученику'}
            </button>
            {editingId && (
              <button
                type="button"
                className="btn btn-outline"
                disabled={saving === 'report'}
                onClick={cancelEdit}
              >
                Отмена
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="billing-section">
        <h2>Журнал занятий</h2>
        {!data?.lesson_reports?.length ? (
          <p className="text-secondary">Пока нет записей.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Тема / заметки</th>
                <th>Минуты</th>
                <th>Ставка</th>
                <th>Сумма</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.lesson_reports.map((r) => (
                <tr key={r.id} className={editingId === r.id ? 'billing-report-row--editing' : undefined}>
                  <td>{formatDateTime(r.created_at)}</td>
                  <td>
                    <div>{r.topic || '—'}</div>
                    {(r.description || r.homework) ? (
                      <div className="billing-report-details">
                        {r.description ? (
                          <div>
                            <strong>Что делали:</strong> {r.description}
                          </div>
                        ) : null}
                        {r.homework ? (
                          <div>
                            <strong>ДЗ:</strong> {r.homework}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td>{r.duration_minutes}</td>
                  <td>{r.rate_per_hour ? `${formatMoney(r.rate_per_hour)}/час` : '—'}</td>
                  <td>{formatMoney(r.amount)}</td>
                  <td>{r.status_label}</td>
                  <td className="billing-report-actions">
                    {(r.status === 'awaiting_student' || r.status === 'charged') && (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline"
                        title="Изменить"
                        aria-label="Изменить"
                        disabled={saving === 'report'}
                        onClick={() => startEdit(r)}
                      >
                        ✎
                      </button>
                    )}
                    {r.status === 'awaiting_student' && (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline"
                        disabled={saving === `cancel-${r.id}`}
                        onClick={() => cancelReport(r.id)}
                      >
                        Отменить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="billing-section">
        <h2>История операций</h2>
        <TransactionsTable items={data?.transactions} />
      </section>
    </div>
  );
}
